import { colors } from "../theme/colors"

/* ---------------------------------------------------------------------------
 * EGDE-330: shared list styling for CMS rich-text bodies.
 *
 * The Figma design marks unordered-list items with the small brand chevron
 * (the GradientChevron icon), not a plain disc — QA rejected the earlier
 * orange-disc fix. The chevron is inlined as a data-URI so every rich-text
 * renderer (Rich Content Block, magazine article body, FAQ answers, vehicle
 * fleet descriptions, …) shows identical bullets on every page.
 *
 * The SVG is authored pointing toward RTL text (inline start = right); the
 * [dir="ltr"] rule mirrors it for LTR locales, matching how the full-size
 * GradientChevron is flipped next to titles.
 * ------------------------------------------------------------------------- */

const CHEVRON_BULLET = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='23' height='38' viewBox='0 0 23 38' fill='none'%3E%3Cdefs%3E%3ClinearGradient id='g' x1='22.2819' y1='19' x2='0' y2='19.015' gradientUnits='userSpaceOnUse'%3E%3Cstop stop-color='%23FFB36E'/%3E%3Cstop offset='1' stop-color='%23F86F3E'/%3E%3C/linearGradient%3E%3C/defs%3E%3Cpath d='M12.4517 2.37161C14.1044 -0.0842343 17.4407 -0.739036 19.9035 0.909074C22.3663 2.55718 23.023 5.88409 21.3702 8.33994L13.1096 20.6143C12.4722 21.5614 12.5281 22.8122 13.2476 23.699L17.7725 29.2765C19.6379 31.5759 19.2809 34.9479 16.975 36.8081C14.6691 38.6682 11.2876 38.3122 9.42213 36.0128L1.1952 25.8721C-0.286324 24.0459 -0.401518 21.4702 0.911101 19.5197L12.4517 2.37161Z' fill='url(%23g)'/%3E%3C/svg%3E")`

export const richTextListStyles = `
  & ul {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  /* EGDE-547: the marker is sized in em so it scales with the text it marks —
     a fixed 8×13px chevron looked right next to 20px body copy but vanished
     next to heading-sized list items (e.g. the Advantages rich-text title).
     The max() floor keeps small body text (16px) at the original 22/8/13px
     instead of shrinking below it. */
  & ul > li {
    position: relative;
    padding-inline-start: max(22px, 1.1em);
  }

  & ul > li::before {
    content: "";
    position: absolute;
    inset-inline-start: 0;
    top: 0.3em;
    width: max(8px, 0.4em);
    height: max(13px, 0.65em);
    background: ${CHEVRON_BULLET} no-repeat center / contain;
  }

  [dir="ltr"] & ul > li::before {
    transform: scaleX(-1);
  }

  /* EGDE-662: the chevron is the house marker, but it had simply taken the plain
     bullet's place with no way back — and editors want both, chosen per list. A
     list of links or short factual items (see the privacy policy) reads better
     with ordinary bullets. So a list the editor MARKS opts out and renders native
     markers instead; everything else keeps the chevron, so no existing content
     changes.

     Two signals, because both are producible with the editor's current Tiptap
     extension set: a "regular-bullets" class (Umb.Tiptap.HtmlAttributeClass) or
     an explicit list-style-type (Umb.Tiptap.HtmlAttributeStyle) — an inline
     list-style-type also keeps whichever marker shape the editor picked, since an
     inline declaration outranks the type in the shorthand below.

     Resetting display to block matters: the chevron variant lays the list out
     with flex, which blockifies the items and would suppress a native marker
     entirely. */
  & ul:is(.regular-bullets, [style*="list-style-type"]) {
    list-style: disc outside;
    display: block;
    padding-inline-start: 1.15em;
  }

  & ul:is(.regular-bullets, [style*="list-style-type"]) > li {
    padding-inline-start: 0;
  }

  & ul:is(.regular-bullets, [style*="list-style-type"]) > li + li {
    margin-block-start: 8px;
  }

  & ul:is(.regular-bullets, [style*="list-style-type"]) > li::before {
    content: none;
  }

  /* Match the ordered list's brand-orange marker so the two plain list styles
     read as one family. */
  & ul:is(.regular-bullets, [style*="list-style-type"]) > li::marker {
    color: ${colors.primitives.primary500};
  }

  /* EGDE-474: any fixed padding budget clips outside markers at some digit
     count ("10." fit in 32px, "100." did not). Inside-positioned NATIVE
     markers size to their content — no digit count can clip — and, unlike a
     CSS counter, keep honouring the start=/value= numbering editors set in
     the RTE. */
  & ol {
    list-style: decimal inside;
    padding: 0;
    margin: 0;
  }

  & ol > li::marker {
    color: ${colors.primitives.primary500};
  }

  /* The RTE wraps item text in a block <p>, which would drop to its own line
     below an inside marker — keep the first block on the marker's line. Any
     further blocks (rare multi-paragraph items, nested lists) stay below. */
  & ol > li > p:first-of-type {
    display: inline;
  }
`
